/**
 * Converts a full location string to an approximate area display
 * PRIVACY: Shows city/town/village + first part of postcode only
 * e.g., "7 Melrose Cl, Maidstone ME15 6BD, UK" => "Maidstone ME15"
 * e.g., "123 High St, Shoreditch, London SW1A 1AA, UK" => "Shoreditch, London SW1A"
 * e.g., "Manchester M1 2GH" => "Manchester M1"
 */
export function getApproximateLocation(fullLocation: string): string {
  if (!fullLocation) return 'Location not set';

  // Extract UK postcode if present (e.g., ME15 6BD, SW1A 1AA, GY1 2JT, M1 2GH)
  const postcodeMatch = fullLocation.match(/\b([A-Z]{1,2}\d{1,2}[A-Z]?)\s?\d[A-Z]{2}\b/i);
  const postcodeArea = postcodeMatch ? postcodeMatch[1] : null;

  // First, remove the postcode from the string to extract city name
  const locationWithoutPostcode = fullLocation.replace(/\b[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}\b/gi, '').trim();

  // Split by comma and clean up
  const parts = locationWithoutPostcode
    .split(',')
    .map(part => part.trim())
    .filter(part => part.length > 0);

  if (parts.length === 0 && !postcodeArea) return 'Location not set';

  // Filter out parts that look like specific addresses
  const cleanParts = parts.filter(part => {
    // Remove US ZIP codes (e.g., 12345, 12345-6789)
    if (/^\d{5}(-\d{4})?$/.test(part)) return false;

    // Remove Canadian postal codes (e.g., A1A 1A1)
    if (/^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i.test(part)) return false;

    // Remove common country codes
    if (['UK', 'USA', 'US', 'United Kingdom', 'United States', 'GB', 'England', 'Scotland', 'Wales'].includes(part)) return false;

    // Remove street addresses (parts that start with numbers or contain "Road", "Street", "Close", etc.)
    if (/^\d+/.test(part)) return false;
    if (/\b(road|rd|street|st|avenue|ave|lane|ln|drive|dr|close|cl|court|ct|way|place|pl|terrace|tce|crescent|cres|boulevard|blvd|highway|hwy)\b/i.test(part)) return false;

    return true;
  });

  let cityName = '';

  if (cleanParts.length === 0) {
    // Only postcode available, return just the area
    return postcodeArea || 'Location not set';
  } else if (cleanParts.length === 1) {
    cityName = cleanParts[0];
  } else {
    // For multiple parts, take the last part (typically the city)
    cityName = cleanParts[cleanParts.length - 1];
  }

  // Add postcode area if available
  if (postcodeArea) {
    return `${cityName} ${postcodeArea}`;
  }

  return cityName;
}

/**
 * Checks if the current user is an accepted member of an event
 */
export function isAcceptedMember(
  eventId: string,
  userId: string | undefined,
  eventPosterId: string,
  applications?: Array<{ userId: string; status: string }>
): boolean {
  if (!userId) return false;

  // Host always sees exact location
  if (userId === eventPosterId) return true;

  // Check if user has an accepted application
  if (applications) {
    const userApp = applications.find(app => app.userId === userId);
    return userApp?.status === 'accepted';
  }

  return false;
}
